import { useState } from "react";
import { Download, X, Smartphone, Apple, Play, Heart, Stars } from "lucide-react";
import { Button } from "../ui/button";
import { motion, AnimatePresence } from "framer-motion";

export const CouplingAppSection = () => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, rotate: -2 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
    }
  };

  return (
    <section
      id="coupling-app"
      className="py-10 lg:py-14 bg-stone-200 relative overflow-hidden"
      data-testid="coupling-app-section"
    >
      <div className="max-w-[95%] mx-auto">
        {/* Box Container with Gray Background - Same as Community Board */}
        <div className="bg-stone-100 rounded-3xl p-4 md:p-6 shadow-lg">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
          >
            {/* Left Content - Text Section */}
            <div className="lg:col-span-5 order-2 lg:order-1 text-center z-10">
              <motion.div variants={itemVariants} className="mb-6">
                <span className="text-gold-500 text-sm font-medium tracking-wider uppercase">
                  Dating App
                </span>
              </motion.div>

            <motion.h2
              variants={itemVariants}
              className="font-sans text-4xl md:text-5xl lg:text-6xl font-[720] mb-6"
            >
              <span className="text-stone-800">Find Your</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-burgundy-500 via-burgundy-600 to-burgundy-500">
                Coupling
              </span>
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-stone-600 text-lg md:text-xl mb-6 leading-relaxed max-w-md mx-auto"
            >
              신뢰와 진정성을 추구하는 사람들을 위한
              <span className="block text-stone-800 font-semibold mt-1">고품격 프리미엄 매칭 앱</span>
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="relative mb-8 py-4 px-6 bg-stone-50 rounded-2xl border border-stone-200 max-w-md mx-auto"
            >
              <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-12 bg-gradient-to-b from-gold-500 to-burgundy-500 rounded-full" />
              <p className="text-burgundy-600 font-medium text-lg md:text-xl italic">
                "싱글링보다 커플링,
                <br />
                웨딩링보다 커플링"
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setShowDownloadModal(true)}
                className="group relative px-8 py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold text-base rounded-full shadow-lg shadow-burgundy-500/25 hover:shadow-xl hover:shadow-burgundy-500/30 transition-all duration-300 flex items-center justify-center gap-3"
                data-testid="coupling-download-btn"
              >
                <Download className="w-5 h-5 group-hover:animate-bounce" />
                앱 다운로드
              </Button>
              
              <Button
                variant="outline"
                className="px-8 py-6 border-2 border-stone-300 bg-white hover:bg-stone-50 text-stone-700 font-medium text-base rounded-full transition-all duration-300"
                data-testid="coupling-learn-more-btn"
              >
                더 알아보기
              </Button>
            </motion.div>

            {/* Platform badges */}
            <motion.div variants={itemVariants} className="mt-8 flex items-center gap-6 justify-center">
              <div className="flex items-center gap-2 text-stone-500 text-sm">
                <Apple className="w-5 h-5" />
                <span>iOS</span>
              </div>
              <div className="w-px h-4 bg-stone-300" />
              <div className="flex items-center gap-2 text-stone-500 text-sm">
                <Play className="w-5 h-5" />
                <span>Android</span>
              </div>
              <div className="w-px h-4 bg-stone-300" />
              <span className="text-xs text-green-500 font-semibold uppercase tracking-wider">Available Now</span>
            </motion.div>

            {/* Beta Notice */}
            <motion.p
              variants={itemVariants}
              className="mt-4 text-xs text-stone-400 text-center"
              data-testid="coupling-beta-notice"
            >
              현재 베타 버전으로 운영됩니다
            </motion.p>
          </div>

          {/* Right Content - Hero Image */}
          <motion.div
            variants={imageVariants}
            className="lg:col-span-7 order-1 lg:order-2 relative"
          >
            {/* Main Image Container */}
            <div className="relative mx-auto max-w-2xl">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-burgundy-200/40 via-gold-200/30 to-burgundy-200/40 rounded-[2rem] blur-3xl transform scale-95" />
              
              {/* Image frame with rings decoration */}
              <div className="relative">
                {/* Decorative ring frame */}
                <div className="absolute -inset-4 border-2 border-burgundy-200/40 rounded-[2.5rem] transform rotate-2" />
                <div className="absolute -inset-8 border border-gold-300/30 rounded-[3rem] transform -rotate-1" />
                
                {/* Main image */}
                <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-stone-300/50">
                  <img
                    src="https://customer-assets.emergentagent.com/job_62e113a6-fe02-48dd-a8bb-4b26ed6daf98/artifacts/p1wq8on5_ChatGPT%20Image%20Mar%2023%2C%202026%2C%2011_28_50%20PM.png"
                    alt="Coupling App - 커플링 매칭"
                    className="w-full h-auto object-cover"
                    data-testid="coupling-app-image"
                  />
                  
                  {/* Subtle overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 via-transparent to-transparent" />

                  {/* Beta badge */}
                  <div
                    className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-md border border-stone-100 whitespace-nowrap"
                    data-testid="coupling-image-beta-badge"
                  >
                    <span className="text-xs font-semibold text-burgundy-600">
                      현재 베타 버전으로 운영됩니다
                    </span>
                  </div>
                </div>

                {/* Floating stats cards */}
                <motion.div
                  variants={floatingVariants}
                  animate="animate"
                  className="absolute -left-4 lg:-left-8 top-1/4 bg-white rounded-2xl p-4 shadow-xl border border-stone-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">매칭 성공률</p>
                      <p className="text-lg font-bold text-stone-800">40%</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  variants={floatingVariants}
                  animate="animate"
                  style={{ animationDelay: "2s" }}
                  className="absolute -right-4 lg:-right-8 bottom-1/4 bg-white rounded-2xl p-4 shadow-xl border border-stone-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img src="https://customer-assets.emergentagent.com/job_9277ad9a-6109-40e4-9d7a-82032fc09839/artifacts/mcdiksg4_image.png" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">프리미엄 회원</p>
                      <p className="text-lg font-bold text-stone-800">1,000</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        </div>
      </div>

      {/* Download Modal */}
      <AnimatePresence>
        {showDownloadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-md p-4"
            onClick={() => setShowDownloadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full overflow-hidden border border-stone-200"
              onClick={(e) => e.stopPropagation()}
              data-testid="download-modal"
            >
              {/* Modal Header */}
              <div className="relative p-8 text-center">
                <button
                  onClick={() => setShowDownloadModal(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-700"
                  data-testid="close-download-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="w-20 h-20 mx-auto rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_9277ad9a-6109-40e4-9d7a-82032fc09839/artifacts/mcdiksg4_image.png" alt="Coupling" className="w-full h-full object-contain" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-1">Coupling</h3>
                <p className="text-burgundy-500 text-sm font-medium">Premium Dating App</p>
              </div>

              {/* Modal Body */}
              <div className="px-8 pb-8">
                <p className="text-stone-500 text-center mb-8">
                  Coupling 앱을 다운로드하고<br />
                  <span className="font-semibold text-stone-800">소중한 인연을 만나보세요!</span>
                </p>

                {/* Download Options */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* App Store */}
                  <a
                    href="https://apps.apple.com/il/app/%EC%BB%A4%ED%94%8C%EB%A7%81-coupling/id6752711029"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-5 bg-stone-800 hover:bg-stone-900 border border-stone-700 text-white rounded-2xl transition-all duration-300 flex flex-col items-center justify-center"
                    data-testid="download-ios"
                  >
                    <Apple className="w-10 h-10 mb-3 text-white/80 group-hover:text-white transition-colors" />
                    <p className="text-xs text-stone-400">Download on</p>
                    <p className="text-sm font-bold">App Store</p>
                  </a>

                  {/* Google Play */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.liz.coupling.apps2&hl=ko"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-5 bg-stone-800 hover:bg-stone-900 border border-stone-700 text-white rounded-2xl transition-all duration-300 flex flex-col items-center justify-center"
                    data-testid="download-android"
                  >
                    <Play className="w-10 h-10 mb-3 text-white/80 group-hover:text-white transition-colors" />
                    <p className="text-xs text-stone-400">GET IT ON</p>
                    <p className="text-sm font-bold">Google Play</p>
                  </a>
                </div>

                {/* QR Code Section */}
                <div className="pt-6 border-t border-stone-200">
                  <p className="text-center text-stone-400 text-sm mb-4">
                    QR 코드를 스캔하세요
                  </p>
                  <div className="flex justify-center">
                    <div className="w-28 h-28 bg-stone-50 border border-stone-200 rounded-xl flex items-center justify-center">
                      <div className="text-center">
                        <Smartphone className="w-8 h-8 text-stone-400 mx-auto mb-1" />
                        <p className="text-xs text-stone-500">QR Code</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* App Available Notice */}
                <div className="mt-6 flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <p className="text-green-600 text-sm font-medium">
                    지금 바로 다운로드 가능합니다
                  </p>
                </div>

                {/* Beta Notice */}
                <p className="mt-3 text-center text-xs text-stone-400" data-testid="coupling-modal-beta-notice">
                  현재 베타 버전으로 운영됩니다
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
